import { DatePickerController } from '@avanta/shared/components/ui/DatePicker';
import { Input } from '@avanta/shared/components/ui/Input';
import { PhoneInputController } from '@avanta/shared/components/ui/PhoneInput';
import { SelectController } from '@avanta/shared/components/ui/Select';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { EMAIL_PATTERN } from '@avanta/shared/constants/patterns';
import { genderOptions } from '@avanta/shared/types/patients';
import { validateAdultBirthDate } from '@avanta/shared/utils/date';
import { validateNIF } from '@avanta/shared/utils/nif';
import { VenusAndMars } from 'lucide-react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { type InputForm } from 'types/form';

export type InsuredPersonProfileForm = {
  firstName: string;
  surname: string;
  email: string;
  nif: string;
  contactPhone: string;
  gender: string;
  dateOfBirth: Date | undefined;
};

type InsuredPersonProfileInputForm = Omit<InputForm, 'component'> & {
  component:
    | typeof Input
    | typeof PhoneInputController
    | typeof SelectController
    | typeof DatePickerController;
};

export const insuredPersonProfileForm: Record<
  keyof InsuredPersonProfileForm,
  InsuredPersonProfileInputForm
> = {
  firstName: {
    label: 'profile:ProfileSettings.Form.firstName.label',
    name: 'firstName',
    type: 'text',
    required: `${LOCIZE_NS.COMMON}:Common.required`,
    placeholder: 'profile:ProfileSettings.Form.firstName.placeholder',
    minLength: {
      value: 3,
      message: 'profile:ProfileSettings.Form.firstName.error',
    },
    component: Input,
  },
  surname: {
    label: 'profile:ProfileSettings.Form.surname.label',
    name: 'surname',
    type: 'text',
    required: `${LOCIZE_NS.COMMON}:Common.required`,
    placeholder: 'profile:ProfileSettings.Form.surname.placeholder',
    minLength: {
      value: 3,
      message: 'profile:ProfileSettings.Form.surname.error',
    },
    component: Input,
  },
  email: {
    label: 'auth:SignupForm.Form.email.label',
    name: 'email',
    type: 'email',
    placeholder: 'auth:SignupForm.Form.email.placeholder',
    required: `${LOCIZE_NS.COMMON}:Common.required`,
    pattern: {
      value: EMAIL_PATTERN,
      message: `${LOCIZE_NS.COMMON}:Common.invalidEmail`,
    },
    component: Input,
  },
  nif: {
    label: 'profile:ProfileSettings.Form.nif.label',
    name: 'nif',
    type: 'text',
    required: `${LOCIZE_NS.COMMON}:Common.required`,
    placeholder: 'profile:ProfileSettings.Form.nif.placeholder',
    validate: value => validateNIF(String(value)) || 'profile:ProfileSettings.Form.nif.error',
    component: Input,
  },
  contactPhone: {
    label: 'profile:ProfileSettings.Form.contactPhone.label',
    name: 'contactPhone',
    type: 'phone',
    placeholder: 'profile:ProfileSettings.Form.contactPhone.placeholder',
    component: PhoneInputController,
    validate: (value: string | number) => {
      const phone = String(value || '');
      if (!phone) return `${LOCIZE_NS.COMMON}:Common.required`;
      return isValidPhoneNumber(phone) || `${LOCIZE_NS.COMMON}:Common.phone.error`;
    },
  },
  gender: {
    label: 'profile:ProfileSettings.Form.gender.label',
    name: 'gender',
    placeholder: 'profile:ProfileSettings.Form.gender.label',
    options: genderOptions,
    required: `${LOCIZE_NS.COMMON}:Common.required`,
    component: SelectController,
    leftIcon: VenusAndMars,
  },
  dateOfBirth: {
    label: 'profile:ProfileSettings.Form.dateOfBirth.label',
    name: 'dateOfBirth',
    placeholder: 'profile:ProfileSettings.Form.dateOfBirth.placeholder',
    required: `${LOCIZE_NS.COMMON}:Common.required`,
    validate: validateAdultBirthDate,
    component: DatePickerController,
  },
};
